/**
 * Role-Based Access Control (RBAC) System
 * Wayne Tech Lab | Planet Earth Help
 */

export enum UserRole {
  GUEST = 0, // Not logged in
  MEMBER = 1, // Free account - logged in user
  ADVANCED = 2, // Advanced user - can modify/post data, has org page
  PRO = 3, // Pro account - verified user with premium features
  ADMIN = 4, // Employee/Admin account
  SUPERADMIN = 5, // Super Admin (root access)
}

export interface RolePermissions {
  canViewPublicContent: boolean;
  canViewDetailedResults: boolean;
  canSaveFavorites: boolean;
  canAccessDashboard: boolean;
  canAccessProFeatures: boolean;
  canModifyData: boolean;
  canManageOwnOrg: boolean;
  canAccessAdmin: boolean;
  canManageUsers: boolean;
  canModifySystemData: boolean;
  canAccessSuperAdmin: boolean;
}

export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  [UserRole.GUEST]: {
    canViewPublicContent: true,
    canViewDetailedResults: false,
    canSaveFavorites: false,
    canAccessDashboard: false,
    canAccessProFeatures: false,
    canModifyData: false,
    canManageOwnOrg: false,
    canAccessAdmin: false,
    canManageUsers: false,
    canModifySystemData: false,
    canAccessSuperAdmin: false,
  },
  [UserRole.MEMBER]: {
    canViewPublicContent: true,
    canViewDetailedResults: true,
    canSaveFavorites: true,
    canAccessDashboard: true,
    canAccessProFeatures: false,
    canModifyData: false,
    canManageOwnOrg: false,
    canAccessAdmin: false,
    canManageUsers: false,
    canModifySystemData: false,
    canAccessSuperAdmin: false,
  },
  [UserRole.ADVANCED]: {
    canViewPublicContent: true,
    canViewDetailedResults: true,
    canSaveFavorites: true,
    canAccessDashboard: true,
    canAccessProFeatures: false,
    canModifyData: true,
    canManageOwnOrg: true,
    canAccessAdmin: false,
    canManageUsers: false,
    canModifySystemData: false,
    canAccessSuperAdmin: false,
  },
  [UserRole.PRO]: {
    canViewPublicContent: true,
    canViewDetailedResults: true,
    canSaveFavorites: true,
    canAccessDashboard: true,
    canAccessProFeatures: true,
    canModifyData: true,
    canManageOwnOrg: true,
    canAccessAdmin: false,
    canManageUsers: false,
    canModifySystemData: false,
    canAccessSuperAdmin: false,
  },
  [UserRole.ADMIN]: {
    canViewPublicContent: true,
    canViewDetailedResults: true,
    canSaveFavorites: true,
    canAccessDashboard: true,
    canAccessProFeatures: true,
    canModifyData: true,
    canManageOwnOrg: true,
    canAccessAdmin: true,
    canManageUsers: true,
    canModifySystemData: true,
    canAccessSuperAdmin: false,
  },
  [UserRole.SUPERADMIN]: {
    canViewPublicContent: true,
    canViewDetailedResults: true,
    canSaveFavorites: true,
    canAccessDashboard: true,
    canAccessProFeatures: true,
    canModifyData: true,
    canManageOwnOrg: true,
    canAccessAdmin: true,
    canManageUsers: true,
    canModifySystemData: true,
    canAccessSuperAdmin: true,
  },
};

export const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.GUEST]: 'Guest',
  [UserRole.MEMBER]: 'Member',
  [UserRole.ADVANCED]: 'Advanced User',
  [UserRole.PRO]: 'Pro Member',
  [UserRole.ADMIN]: 'Admin',
  [UserRole.SUPERADMIN]: 'Super Admin',
};

export const ROLE_COLORS: Record<UserRole, string> = {
  [UserRole.GUEST]: 'gray',
  [UserRole.MEMBER]: 'blue',
  [UserRole.ADVANCED]: 'green',
  [UserRole.PRO]: 'purple',
  [UserRole.ADMIN]: 'orange',
  [UserRole.SUPERADMIN]: 'red',
};

/**
 * Get permissions for a given role
 */
export function getPermissions(role: UserRole): RolePermissions {
  return ROLE_PERMISSIONS[role];
}

/**
 * Check if user has specific permission
 */
export function hasPermission(role: UserRole, permission: keyof RolePermissions): boolean {
  return ROLE_PERMISSIONS[role][permission];
}

/**
 * Check if user role meets minimum requirement
 */
export function hasMinimumRole(userRole: UserRole, minimumRole: UserRole): boolean {
  return userRole >= minimumRole;
}

/**
 * Parse configured superadmin emails from Vite env.
 */
function getConfiguredSuperAdminEmails(): string[] {
  const raw = import.meta.env.VITE_SUPERADMIN_EMAILS;
  if (!raw || typeof raw !== 'string') return [];

  return raw
    .split(',')
    .map((email) => email.toLowerCase().trim())
    .filter(Boolean);
}

/**
 * Get role from email during initial bootstrap.
 * Superadmin eligibility is controlled via VITE_SUPERADMIN_EMAILS.
 */
export function getInitialRoleFromEmail(email: string): UserRole {
  const normalizedEmail = email.toLowerCase().trim();

  if (getConfiguredSuperAdminEmails().includes(normalizedEmail)) {
    return UserRole.SUPERADMIN;
  }

  // All other users start as members
  return UserRole.MEMBER;
}

/**
 * Check if email is authorized for elevated role
 */
export function isAuthorizedEmail(email: string, role: UserRole): boolean {
  const normalizedEmail = email.toLowerCase().trim();

  if (role === UserRole.SUPERADMIN) {
    return getConfiguredSuperAdminEmails().includes(normalizedEmail);
  }

  return true; // Other roles can be assigned by admins
}
